import { DialogComponent, DialogOpenEvent, restoreScrollPosition } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  #getLockedScrollY() {
    const lockedTop = document.body.style.top;

    if (document.body.style.position === 'fixed' && lockedTop) {
      return Math.abs(parseFloat(lockedTop));
    }

    return window.scrollY;
  }

  #forceCleanup(scrollY = null) {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const lockedTop = document.body.style.top;
    const shouldRestoreLockedScroll = document.body.style.position === 'fixed' && lockedTop;
    const restoreScrollY = scrollY ?? (shouldRestoreLockedScroll ? Math.abs(parseFloat(lockedTop)) : null);
    const dialogs = new Set(
      [dialog, ...this.querySelectorAll('dialog'), ...document.querySelectorAll('cart-drawer-component dialog')].filter(
        (element) => element instanceof HTMLDialogElement
      )
    );

    for (const activeDialog of dialogs) {
      activeDialog.classList.remove('dialog-closing');
      activeDialog.style.animation = '';
      activeDialog.style.pointerEvents = '';

      if (activeDialog.open) {
        activeDialog.close();
      }

      activeDialog.removeAttribute('open');
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');

    if (restoreScrollY !== null) {
      restoreScrollPosition(restoreScrollY);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.addEventListener('submit', this.#handleCheckoutSubmit);
    this.addEventListener('click', this.#handleCheckoutClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.removeEventListener('submit', this.#handleCheckoutSubmit);
    this.removeEventListener('click', this.#handleCheckoutClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  /**
   * Handles cart update events - opens drawer only after product-form adds and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { source?: string } }>} event
   */
  #handleCartAdd = (event) => {
    const isProductFormAdd = event.detail.data?.source === 'product-form-component';

    if (this.hasAttribute('auto-open') && isProductFormAdd) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  #handleCheckoutSubmit = async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches('.cart-form')) return;

    const submitter = event.submitter;
    const checkoutButton = /** @type {HTMLButtonElement | null} */ (
      submitter instanceof HTMLButtonElement && submitter.name === 'checkout'
        ? submitter
        : this.querySelector('button[name="checkout"]')
    );

    if (!checkoutButton) return;

    event.preventDefault();

    this.#setCheckoutLoading(checkoutButton, true);

    const termsInput = this.querySelector('.cart-drawer__terms-input');
    if (termsInput instanceof HTMLInputElement) {
      try {
        await fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              [termsInput.name.replace(/^attributes\[(.*)\]$/, '$1')]: termsInput.value || 'accepted',
            },
          }),
        });
      } catch (_error) {
        // The checkout should still proceed; the attribute is non-critical UI consent metadata.
      }
    }

    window.location.assign(checkoutButton.dataset.checkoutUrl || `${Theme.routes.cart_url}/checkout`);
  };

  /**
   * Intercepts checkout button clicks.
   * - If consent checkbox is unchecked: do nothing — let the browser run native constraint
   *   validation and show its "Please check this box" tooltip.
   * - If consent checkbox is checked (or absent): prevent native form submit, show spinner,
   *   persist consent to cart attributes, then navigate to checkout.
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = async (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof HTMLElement ? event.target.closest('button[name="checkout"]') : null
    );
    if (!button) return;

    const termsInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.cart-drawer__terms-input'));

    // Checkbox exists but not checked — let browser show native validation tooltip
    if (termsInput && !termsInput.checked) return;

    // Checkbox is checked (or no checkbox) — take over and handle checkout ourselves
    event.preventDefault();

    if (button.disabled || button.classList.contains('is-loading')) return;

    this.#setCheckoutLoading(button, true);

    if (termsInput) {
      try {
        await fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              [termsInput.name.replace(/^attributes\[(.*)\]$/, '$1')]: termsInput.value || 'accepted',
            },
          }),
        });
      } catch (_error) {
        // Non-critical — proceed to checkout regardless
      }
    }

    window.location.assign(button.dataset.checkoutUrl || `${Theme.routes.cart_url}/checkout`);
  };

  #handlePageShow = () => {
    this.querySelectorAll('.cart__checkout-button.is-loading').forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        this.#setCheckoutLoading(button, false);
      }
    });
  };

  #setCheckoutLoading(button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.toggleAttribute('aria-busy', isLoading);
    button.disabled = isLoading;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    const scrollY = this.#getLockedScrollY();

    this.closeDialog().finally(() => {
      this.#forceCleanup(scrollY);
      requestAnimationFrame(() => this.#forceCleanup(scrollY));
      setTimeout(() => this.#forceCleanup(scrollY), 50);
    });
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
